import { useApi } from '@backstage/core-plugin-api';
import { DocRatingSummary } from '@internal/backstage-plugin-doc-feedback-common';
import { Button, Typography, makeStyles } from '@material-ui/core';
import ThumbDownOutlinedIcon from '@material-ui/icons/ThumbDownOutlined';
import ThumbUpOutlinedIcon from '@material-ui/icons/ThumbUpOutlined';
import { useEffect, useState } from 'react';
import { docFeedbackApiRef } from '../../api';
import { openFeedbackDialog, useDocPage } from './useDocPage';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginTop: theme.spacing(6),
    paddingTop: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  question: {
    fontWeight: theme.typography.fontWeightMedium as number,
    marginRight: theme.spacing(1),
  },
  selected: {
    backgroundColor: theme.palette.action.selected,
    borderColor: theme.palette.primary.main,
  },
  count: {
    marginLeft: theme.spacing(1),
    paddingLeft: theme.spacing(1),
    borderLeft: `1px solid ${theme.palette.divider}`,
    fontWeight: theme.typography.fontWeightBold as number,
  },
  meta: {
    color: theme.palette.text.secondary,
  },
}));

/**
 * "Was this page helpful?" bar rendered at the bottom of each docs page (inside
 * the TechDocs shadow DOM). The thumbs up/down counts are public so every
 * reader can see how effective the page is.
 */
export const HelpfulAddon = () => {
  const classes = useStyles();
  const api = useApi(docFeedbackApiRef);
  const { entityRef, pagePath } = useDocPage();
  const [summary, setSummary] = useState<DocRatingSummary>();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setSummary(undefined);
    setError(false);
    api
      .getRatingSummary({ entityRef, pagePath })
      .then(result => !cancelled && setSummary(result))
      .catch(() => !cancelled && setError(true));
    return () => {
      cancelled = true;
    };
  }, [api, entityRef, pagePath]);

  const vote = async (helpful: boolean) => {
    setSubmitting(true);
    setError(false);
    try {
      setSummary(await api.rate({ entityRef, pagePath, helpful }));
      if (!helpful) {
        openFeedbackDialog({ category: 'inaccuracy' });
      }
    } catch {
      setError(true);
    } finally {
      setSubmitting(false);
    }
  };

  const userVote = summary?.userVote;
  const count = (value: number | undefined) =>
    summary ? <span className={classes.count}>{value}</span> : null;
  const label = (answer: string, value: number | undefined, verb: string) =>
    summary ? `${answer}, ${value} ${value === 1 ? 'reader' : 'readers'} ${verb}` : answer;

  return (
    <div className={classes.root} data-testid="doc-helpful-bar">
      <Typography variant="body1" className={classes.question}>
        Was this page helpful?
      </Typography>
      <Button
        size="small"
        variant="outlined"
        startIcon={<ThumbUpOutlinedIcon />}
        aria-label={label('Yes', summary?.helpful, 'found this page helpful')}
        aria-pressed={userVote === true}
        className={userVote === true ? classes.selected : undefined}
        disabled={submitting}
        onClick={() => vote(true)}
      >
        Yes
        {count(summary?.helpful)}
      </Button>
      <Button
        size="small"
        variant="outlined"
        startIcon={<ThumbDownOutlinedIcon />}
        aria-label={label('No', summary?.notHelpful, 'did not find this page helpful')}
        aria-pressed={userVote === false}
        className={userVote === false ? classes.selected : undefined}
        disabled={submitting}
        onClick={() => vote(false)}
      >
        No
        {count(summary?.notHelpful)}
      </Button>
      <Typography variant="body2" className={classes.meta}>
        {error && 'Could not save your vote, please try again.'}
        {!error && userVote === true && 'Thanks for letting us know!'}
        {!error && userVote === false && (
          <>
            Sorry about that.{' '}
            <Button
              size="small"
              color="primary"
              onClick={() => openFeedbackDialog({ category: 'inaccuracy' })}
            >
              Tell us what to improve
            </Button>
          </>
        )}
      </Typography>
    </div>
  );
};
